import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';

import { cn } from 'src/lib/utils';
import { toggleVariants } from 'src/components/ui/toggle';

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & {
    type?: 'single' | 'multiple';
  }
>({
  size: 'default',
  variant: 'default',
  type: 'single',
});

function ToggleGroup({
  className,
  variant,
  size,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> & VariantProps<typeof toggleVariants>) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      className={cn(
        'group/toggle-group flex w-fit items-center rounded-md data-[variant=outline]:shadow-xs',
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size }}>{children}</ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        'min-w-0 flex-1 shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus:z-10 focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l',
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

function ToggleGroupCard({
  children,
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root>) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      className={cn(
        'group/toggle-group',
        {
          ['flex flex-col gap-4 sm:flex-row']: orientation === 'horizontal',
          ['space-y-3']: orientation === 'vertical',
        },
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant: 'default', size: 'custom', type: props.type }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

function ToggleGroupCardItem({
  children,
  className,
  icon: Icon,
  title,
  description,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> & {
  icon: React.ElementType;
  title: string;
  description?: string;
}) {
  const { type } = React.useContext(ToggleGroupContext);

  return (
    <ToggleGroupPrimitive.Item className={cn(className)} {...props} asChild>
      <div
        className={cn(
          'group/toggle-group-card-item relative border border-border rounded-xl shadow-xs bg-white flex gap-4 p-5 select-none hover:bg-accent transition-colors data-[state=on]:border-accent-software-escrow data-[state=on]:bg-[color-mix(in_srgb,var(--color-accent-software-escrow)_6%,var(--color-white))] data-[state=on]:shadow-[0_0_0_1px_var(--color-accent-software-escrow)] data-[state=off]:cursor-pointer flex-1',
        )}
      >
        <div className="size-10 rounded-md bg-muted flex items-center justify-center shrink-0 text-foreground transition-colors group-data-[state=on]/toggle-group-card-item:bg-accent-software-escrow">
          <Icon className="size-5 text-slate-700 group-data-[state=on]/toggle-group-card-item:text-white" />
        </div>
        <div className="text-left not-has-[p]:flex not-has-[p]:flex-col not-has-[p]:justify-center space-y-1">
          <h3 className="text-base font-extrabold text-foreground">{title}</h3>
          {description && <p className="text-sm text-muted-foreground">{description}</p>}
        </div>
        <div
          className={cn(
            'size-5 border-border border-2 bg-white flex items-center justify-center transition-colors group-data-[state=on]/toggle-group-card-item:border-accent-software-escrow group-data-[state=on]/toggle-group-card-item:bg-accent-software-escrow group-data-[state=off]/toggle-group-card-item:[&>svg]:hidden shrink-0 ml-auto',
            {
              'rounded-full': type === 'single',
              'rounded-sm': type === 'multiple',
            },
          )}
        >
          <Check className="size-3 text-white" />
        </div>
      </div>
    </ToggleGroupPrimitive.Item>
  );
}

export { ToggleGroup, ToggleGroupItem, ToggleGroupCard, ToggleGroupCardItem };
